import {
  REWARD_POINTS,
  type RewardItem,
  type RewardSource,
  rewardShelves,
  sparksShort,
  useRewardActions,
  useRewardHistory,
  useRewardWallet,
} from '@shkola/api';
import { REWARD_SKIES, type SubjectIcon } from '@shkola/design';
import { RocketLaunchIcon } from '@phosphor-icons/react/RocketLaunch';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrentContext } from '../app/ViewContext';
import {
  Block,
  DARK,
  DEEP,
  GRAD,
  type Grad,
  Head,
  INK,
  INK_FAINT,
  INK_SOFT,
  LINE,
  PAPER,
  PageHead,
  SubjectArt,
  pillClass,
} from '../design/forms';
import {
  ArrowCounterClockwiseIcon,
  BookOpenTextIcon,
  CaretDownIcon,
  CheckCircleIcon,
  type FilledIcon,
  GraduationCapIcon,
  NotePencilIcon,
  PuzzlePieceIcon,
  SparkleIcon,
} from '../design/icons';
import { FaceAvatar, useFace } from './home/LineArt';
import { IconDot, Label, LifeArt, type LifeArtKind } from './LifeArt';

/*
 * «Магазин нагород» in «Форми 2» (28.10). The title on the paper; on top the student as on the home — the
 * face they picked in its ring, the buddy at its side, on the sky they wear — and the sparks big and
 * light; how to earn them beside. Then the shelves — the frames, the skies, the buddies — as soft tiles
 * on the paper: the thing itself in our one line (the ring round the face, a piece of the sky, the buddy
 * drawn as the subjects are), the price, one button. What the sparks were for — a ruled list at the side.
 */

/** Where the sparks come from: an icon in its colour (no emoji). */
const SOURCE: Record<RewardSource, { icon: FilledIcon; tone: Grad }> = {
  homework: { icon: NotePencilIcon, tone: 'cobalt' },
  puzzle: { icon: PuzzlePieceIcon, tone: 'butter' },
  review: { icon: ArrowCounterClockwiseIcon, tone: 'mint' },
  season: { icon: RocketLaunchIcon, tone: 'lavender' },
  catchUp: { icon: BookOpenTextIcon, tone: 'apricot' },
  nmt: { icon: GraduationCapIcon, tone: 'sky' },
};

/** The ring round the face: our deep gradients, the rainbow of five of them. */
const FRAME: Record<string, string> = {
  'frame-mint': `linear-gradient(135deg, ${DEEP.mint[0]} 0%, ${DEEP.mint[1]} 100%)`,
  'frame-coral': `linear-gradient(135deg, ${DEEP.coral[0]} 0%, ${DEEP.coral[1]} 100%)`,
  'frame-sky': `linear-gradient(135deg, ${DEEP.sky[0]} 0%, ${DEEP.sky[1]} 100%)`,
  'frame-gold': `linear-gradient(135deg, ${DEEP.butter[0]} 0%, ${DEEP.butter[1]} 100%)`,
  'frame-rainbow': `conic-gradient(from 200deg, ${DEEP.coral[1]}, ${DEEP.butter[1]}, ${DEEP.mint[1]}, ${DEEP.sky[1]}, ${DEEP.lavender[1]}, ${DEEP.coral[1]})`,
};

/** The buddies drawn in our one line: a subject's picture where there is one, our own where not. */
const BUDDY: Record<string, { tone: Grad; subject?: SubjectIcon; art?: LifeArtKind }> = {
  'buddy-seedling': { tone: 'mint', subject: 'Sprout' },
  'buddy-music': { tone: 'pink', subject: 'Music' },
  'buddy-ball': { tone: 'lime', subject: 'Dumbbell' },
  'buddy-palette': { tone: 'apricot', subject: 'Palette' },
  'buddy-rocket': { tone: 'lavender', art: 'rocket' },
  'buddy-rainbow': { tone: 'sky', art: 'rainbow' },
  'buddy-atom': { tone: 'cobalt', subject: 'Atom' },
  'buddy-telescope': { tone: 'lavender', subject: 'Telescope' },
  'buddy-sunglasses': { tone: 'butter', art: 'shades' },
  'buddy-crown': { tone: 'butter', art: 'crown' },
};

const when = (iso: string) =>
  new Intl.DateTimeFormat('uk-UA', {
    timeZone: 'Europe/Kyiv',
    day: 'numeric',
    month: 'short',
  }).format(new Date(iso));

/** The sky as a soft card: the white light in its corner, as every gradient of the look. */
const skyOf = (id: string) =>
  `radial-gradient(90% 70% at 0% 0%, rgb(255 255 255 / 0.6) 0%, rgb(255 255 255 / 0) 60%), ${REWARD_SKIES[id] ?? ''}`;

function Buddy({ id, size }: { id: string; size: number }) {
  const b = BUDDY[id];
  if (!b) return null;
  return b.subject ? (
    <SubjectArt icon={b.subject} tone={b.tone} size={size} />
  ) : (
    <LifeArt kind={b.art ?? 'rocket'} tone={b.tone} size={size} />
  );
}

/** The student's face in a ring of the frame (or none). */
function Framed({
  studentId,
  initials,
  frame,
  size,
}: {
  studentId: string;
  initials: string;
  frame?: string | null;
  size: number;
}) {
  const [face] = useFace(studentId);
  const ring = frame ? FRAME[frame] : undefined;
  const pad = Math.max(3, Math.round(size / 22));
  return (
    <span
      className="inline-flex shrink-0 rounded-full"
      style={ring ? { backgroundImage: ring, padding: pad } : { padding: pad }}
    >
      <span className="inline-flex rounded-full bg-white" style={{ padding: pad }}>
        <FaceAvatar face={face} initials={initials} size={size} />
      </span>
    </span>
  );
}

/** What the item looks like: the ring round the face, a piece of the sky, the buddy. */
function Preview({
  item,
  studentId,
  initials,
}: {
  item: RewardItem;
  studentId: string;
  initials: string;
}) {
  if (item.slot === 'frame')
    return <Framed studentId={studentId} initials={initials} frame={item.id} size={58} />;
  if (item.slot === 'sky')
    return (
      <span
        className="block h-[68px] w-full max-w-[120px] rounded-[16px]"
        style={{ backgroundImage: skyOf(item.id) }}
      />
    );
  return <Buddy id={item.id} size={64} />;
}

function ItemCard({
  item,
  owned,
  worn,
  balance,
  self,
  studentId,
  initials,
}: {
  item: RewardItem;
  owned: boolean;
  worn: boolean;
  balance: number;
  self: boolean;
  studentId: string;
  initials: string;
}) {
  const { t } = useTranslation();
  const actions = useRewardActions(studentId);
  const short = sparksShort(balance, item);
  const busy = actions.buy.isPending || actions.wear.isPending;
  return (
    <li
      className="flex min-w-0 flex-col items-center gap-2 rounded-[22px] p-3 pt-4 text-center"
      style={{
        backgroundColor: worn ? '#FFFFFF' : PAPER,
        boxShadow: worn ? `inset 0 0 0 2px ${DARK}` : undefined,
      }}
    >
      <span className="flex h-[76px] w-full items-center justify-center">
        <Preview item={item} studentId={studentId} initials={initials} />
      </span>
      <span className="text-[14px] leading-[18px] font-medium" style={{ color: INK }}>
        {item.name}
      </span>
      <span className="mt-auto flex min-h-9 flex-col items-center justify-end gap-1.5">
        {owned ? (
          worn ? (
            <Label tone="lime">{t('rewards.worn')}</Label>
          ) : (
            !self && <CheckCircleIcon size={18} weight="fill" aria-hidden color={GRAD.mint.ink} />
          )
        ) : (
          <span
            className="inline-flex items-center gap-1 text-[13px] font-medium"
            style={{ color: short > 0 ? INK_FAINT : GRAD.lavender.ink }}
          >
            <SparkleIcon size={13} weight="fill" aria-hidden />
            {item.price}
          </span>
        )}
        {self &&
          (owned ? (
            worn ? (
              <button
                type="button"
                disabled={busy}
                onClick={() => actions.wear.mutate({ slot: item.slot, item: null })}
                className={`${pillClass('light', 'sm')} pr-3.5`}
              >
                {t('rewards.takeOff')}
              </button>
            ) : (
              <button
                type="button"
                disabled={busy}
                onClick={() => actions.wear.mutate({ slot: item.slot, item: item.id })}
                className={`${pillClass('light', 'sm')} pr-3.5`}
              >
                {t('rewards.wear')}
              </button>
            )
          ) : short > 0 ? (
            <span className="text-[12px] leading-4" style={{ color: INK_FAINT }}>
              {t('rewards.short', { count: short })}
            </span>
          ) : (
            <button
              type="button"
              disabled={busy}
              onClick={() =>
                void actions.buy
                  .mutateAsync(item.id)
                  .then(() => actions.wear.mutateAsync({ slot: item.slot, item: item.id }))
                  .catch(() => undefined)
              }
              className={`${pillClass('dark', 'sm')} pr-3.5 disabled:opacity-35`}
            >
              {t('rewards.buy')}
            </button>
          ))}
      </span>
    </li>
  );
}

/** «Магазин нагород»: sparks for effort, spent on looks of one's own pages. */
export function RewardsPage() {
  const { t } = useTranslation();
  const ctx = useCurrentContext();
  const child = ctx.kind === 'student' || ctx.kind === 'guardian' ? ctx.child : undefined;
  const studentId = child?.memberId ?? '';
  const self = ctx.kind === 'student';
  const wallet = useRewardWallet(studentId);
  const history = useRewardHistory(studentId).data ?? [];
  const [showAll, setShowAll] = useState(false);
  if (!child) return null;
  const w = wallet.data;
  const initials = `${child.firstName[0] ?? ''}${child.lastName[0] ?? ''}`.toUpperCase();
  const shown = showAll ? history : history.slice(0, 8);

  return (
    <div className="flex flex-col gap-4">
      <PageHead title={t('rewards.title')} />
      {wallet.isPending ? (
        <div className="h-[260px] animate-pulse rounded-[28px] bg-white" aria-busy />
      ) : !w ? null : (
        <>
          <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[minmax(0,1fr)_380px]">
            <div className="flex min-w-0 flex-col gap-4">
              {/* the student as on the home, on the sky they wear; the sparks big and light */}
              <section
                className="relative isolate flex min-w-0 flex-col gap-6 overflow-hidden rounded-[28px] bg-white p-5 sm:flex-row sm:items-center md:p-7"
                style={w.worn.sky ? { backgroundImage: skyOf(w.worn.sky) } : undefined}
              >
                <span className="relative mx-auto shrink-0 sm:mx-0">
                  <Framed
                    studentId={studentId}
                    initials={initials}
                    frame={w.worn.frame}
                    size={132}
                  />
                  {w.worn.buddy && (
                    <span
                      className="absolute -right-4 -bottom-2 rounded-full p-1.5"
                      style={{ backgroundColor: w.worn.sky ? 'rgb(255 255 255 / 0.8)' : PAPER }}
                    >
                      <Buddy id={w.worn.buddy} size={52} />
                    </span>
                  )}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-[14px] font-medium" style={{ color: INK_SOFT }}>
                    {t('rewards.hand')}
                  </p>
                  <p
                    className="flex items-center gap-3 font-display text-[64px] leading-[70px] font-light tracking-[-0.05em] md:text-[80px] md:leading-[86px]"
                    style={{ color: INK }}
                  >
                    {t('rewards.sparks', { count: w.balance })}
                  </p>
                  <p className="mt-1">
                    <Label tone="lavender" icon={SparkleIcon}>
                      {t('rewards.totals', { earned: w.earned, spent: w.spent })}
                    </Label>
                  </p>
                  <p
                    className="mt-4 max-w-xl text-[14.5px] leading-[22px]"
                    style={{ color: INK_SOFT }}
                  >
                    {self ? t('rewards.intro') : t('rewards.introFamily')}
                  </p>
                </div>
              </section>
              {rewardShelves().map((shelf) => (
                <Block key={shelf.slot}>
                  <Head title={t(`rewards.slot.${shelf.slot}`)} />
                  <ul className="grid grid-cols-2 gap-2 sm:grid-cols-3 xl:grid-cols-5">
                    {shelf.items.map((item) => (
                      <ItemCard
                        key={item.id}
                        item={item}
                        owned={w.owned.includes(item.id)}
                        worn={w.worn[item.slot] === item.id}
                        balance={w.balance}
                        self={self}
                        studentId={studentId}
                        initials={initials}
                      />
                    ))}
                  </ul>
                </Block>
              ))}
            </div>
            <div className="flex min-w-0 flex-col gap-4">
              <Block>
                <Head title={t('rewards.how')} />
                <ul className="flex flex-col">
                  {(Object.keys(REWARD_POINTS) as RewardSource[]).map((k) => (
                    <li
                      key={k}
                      className="flex items-center gap-3 py-2"
                      style={{ borderTop: `1px solid ${LINE}` }}
                    >
                      <IconDot icon={SOURCE[k].icon} tone={SOURCE[k].tone} size={32} />
                      <span className="min-w-0 flex-1 text-[14.5px]" style={{ color: INK }}>
                        {t(`rewards.source.${k}`)}
                      </span>
                      <span
                        className="inline-flex items-center gap-1 text-[14px] font-medium"
                        style={{ color: GRAD.lavender.ink }}
                      >
                        +{REWARD_POINTS[k]}
                        <SparkleIcon size={12} weight="fill" aria-hidden />
                      </span>
                    </li>
                  ))}
                </ul>
              </Block>
              <Block>
                <Head title={t('rewards.history')} count={history.length} />
                {history.length === 0 ? (
                  <p className="text-[14px]" style={{ color: INK_SOFT }}>
                    {t('rewards.noHistory')}
                  </p>
                ) : (
                  <ul className="flex flex-col">
                    {shown.map((d, i) => (
                      <li
                        key={`${d.at}-${i}`}
                        className="flex items-center gap-3 py-2.5"
                        style={{ borderTop: `1px solid ${LINE}` }}
                      >
                        <IconDot
                          icon={SOURCE[d.source].icon}
                          tone={SOURCE[d.source].tone}
                          size={32}
                        />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-[14px]" style={{ color: INK }}>
                            {t(`rewards.source.${d.source}`)}
                            {d.title && (
                              <span style={{ color: INK_SOFT }}>
                                {' · '}
                                {d.source === 'season'
                                  ? t('rewards.station', { n: d.title })
                                  : d.title}
                              </span>
                            )}
                          </span>
                          <span className="block text-[12px]" style={{ color: INK_FAINT }}>
                            {when(d.at)}
                          </span>
                        </span>
                        <span
                          className="text-[14px] font-medium"
                          style={{ color: GRAD.lavender.ink }}
                        >
                          +{d.points}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
                {history.length > shown.length && (
                  <button
                    type="button"
                    onClick={() => setShowAll(true)}
                    className="mt-2 flex items-center gap-2 self-start py-1 text-[14px] font-medium"
                    style={{ color: INK }}
                  >
                    {t('rewards.showAll')}
                    <span className="text-[13px] font-normal" style={{ color: INK_FAINT }}>
                      {history.length - shown.length}
                    </span>
                    <CaretDownIcon size={12} weight="bold" aria-hidden />
                  </button>
                )}
              </Block>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
